export type ScriptTimelineSectionKind = "scene" | "gap";

export interface ScriptTimelineSection {
    id: string;
    kind: ScriptTimelineSectionKind;
    start: number;
    end: number;
    sceneIndex: number | null;
	/** Stable logical-scene identity (`root` or the scene-start split id). */
	sceneIdentity: string | null;
    label?: string;
    gapInsertionId?: string;
}

export interface ScriptTimelineSceneBoundary {
    id: string;
    splitId: string;
    sourceTime: number;
    time: number;
    previousSceneIndex: number;
    sceneIndex: number;
}

export interface ScriptTimelineEditBoundary {
    id: string;
    splitId: string;
    sourceTime: number;
    time: number;
}

export interface ScriptTimelineGapSection {
    id: string;
    insertionId: string;
    start: number;
    end: number;
}

export interface ScriptTimelineModel {
    duration: number;
    sections: ScriptTimelineSection[];
    sceneBoundaries: ScriptTimelineSceneBoundary[];
    editBoundaries: ScriptTimelineEditBoundary[];
    gaps: ScriptTimelineGapSection[];
}

export type ScriptTimelineRange = { start: number; end: number };
export type ScriptTimelineRangeDragMode =
    | "create"
    | "resize-start"
    | "resize-end"
    | "move";

export const SCRIPT_TIMELINE_RANGE_DRAG_THRESHOLD_PX = 3;

interface BuildScriptTimelineModelOptions {
    clip: any;
    duration: number;
    mapSourceBoundaryToTimeline: (sourceTime: number) => number | null;
}

const EPS = 1e-3;
const SCRIPT_TIMELINE_MIN_RANGE_SEC = 0.01;

const clamp = (value: number, min: number, max: number) =>
    Math.max(min, Math.min(max, value));

const uniqueByTimelineTime = <T extends { time: number }>(items: T[]): T[] => {
    const output: T[] = [];
    for (const item of items.sort((a, b) => a.time - b.time)) {
        if (!output.length || Math.abs(item.time - output[output.length - 1].time) > EPS) {
            output.push(item);
        }
    }
    return output;
};

/**
 * Scene joins can collapse onto the same timeline pixel after source cuts.
 * The later boundary owns the content that follows the join, so retain it.
 */
const uniqueSceneBoundariesByTimelineTime = (
    items: ScriptTimelineSceneBoundary[],
): ScriptTimelineSceneBoundary[] => {
    const output: ScriptTimelineSceneBoundary[] = [];
    for (const item of items.sort((a, b) => a.time - b.time || a.sourceTime - b.sourceTime)) {
        const previous = output[output.length - 1];
        if (previous && Math.abs(item.time - previous.time) <= EPS) {
            output[output.length - 1] = item;
        } else {
            output.push(item);
        }
    }
    return output;
};

/**
 * Build the semantic model used by every ScriptClip interaction.
 *
 * Scene boundaries and inserted gaps divide the selectable spine. Ordinary
 * edit points remain visible, but deliberately do not create a second notion
 * of "scene" for hover/click selection.
 */
export const buildScriptTimelineModel = ({
    clip,
    duration,
    mapSourceBoundaryToTimeline,
}: BuildScriptTimelineModelOptions): ScriptTimelineModel => {
    const safeDuration = Number.isFinite(duration) ? Math.max(0, duration) : 0;
    const empty: ScriptTimelineModel = {
        duration: safeDuration,
        sections: [],
        sceneBoundaries: [],
        editBoundaries: [],
        gaps: [],
    };
    if (!clip || safeDuration <= EPS) return empty;

    const clipInSec = Number(clip?.range?.[0]?.seconds ?? 0);
    const clipOutSec = Number(
        clip?.range?.[1]?.seconds ?? clip?.duration?.seconds ?? clipInSec,
    );
    const clipStartSec = Number(clip?.start?.seconds ?? 0);
    const rawSplits: any[] = Array.isArray(clip?.splits) ? clip.splits : [];

    const sceneSplits = rawSplits
        .filter((split) => split?.isSceneBoundary === true)
        .map((split) => ({
            split,
            sourceTime: Number(split?.timeSec ?? Number.NaN),
        }))
        .filter(
            ({ sourceTime }) =>
                Number.isFinite(sourceTime) &&
                sourceTime > clipInSec + EPS &&
                sourceTime < clipOutSec - EPS,
        )
        .sort((a, b) => a.sourceTime - b.sourceTime);

    const sceneBoundaries = uniqueSceneBoundariesByTimelineTime(
        sceneSplits
            .map(({ split, sourceTime }, splitIndex) => {
                const mapped = mapSourceBoundaryToTimeline(sourceTime);
                if (mapped == null || !Number.isFinite(mapped)) return null;
                const time = clamp(mapped, 0, safeDuration);
                if (time <= EPS || time >= safeDuration - EPS) return null;
                return {
                    id: `scene-boundary:${String(split?.id ?? splitIndex)}`,
                    splitId: String(split?.id ?? splitIndex),
                    sourceTime,
                    time,
                    previousSceneIndex: splitIndex,
                    sceneIndex: splitIndex + 1,
                } satisfies ScriptTimelineSceneBoundary;
            })
            .filter((boundary): boundary is ScriptTimelineSceneBoundary => boundary !== null),
    );

    const editBoundaries = uniqueByTimelineTime(
        rawSplits
            .filter((split) => split?.isSceneBoundary !== true)
            .map((split, splitIndex) => {
                const sourceTime = Number(split?.timeSec ?? Number.NaN);
                if (
                    !Number.isFinite(sourceTime) ||
                    sourceTime <= clipInSec + EPS ||
                    sourceTime >= clipOutSec - EPS
                ) {
                    return null;
                }
                const mapped = mapSourceBoundaryToTimeline(sourceTime);
                if (mapped == null || !Number.isFinite(mapped)) return null;
                const time = clamp(mapped, 0, safeDuration);
                if (time <= EPS || time >= safeDuration - EPS) return null;
                return {
                    id: `edit-boundary:${String(split?.id ?? splitIndex)}`,
                    splitId: String(split?.id ?? splitIndex),
                    sourceTime,
                    time,
                } satisfies ScriptTimelineEditBoundary;
            })
            .filter((boundary): boundary is ScriptTimelineEditBoundary => boundary !== null),
    );

    const gaps: ScriptTimelineGapSection[] = [];
    const insertions: any[] = Array.isArray(clip?.gapInsertions) ? clip.gapInsertions : [];
    const segments: any[] = Array.isArray(clip?.scriptSegments) ? clip.scriptSegments : [];
	const sortedSegments = [...segments].sort(
		(first, second) =>
			Number(first?.srcStartSec ?? Number.POSITIVE_INFINITY) -
				Number(second?.srcStartSec ?? Number.POSITIVE_INFINITY) ||
			Number(first?.compStartSec ?? Number.POSITIVE_INFINITY) -
				Number(second?.compStartSec ?? Number.POSITIVE_INFINITY),
	);
	type GapInsertionRecord = {
		insertionId: string;
		atSec: number;
		duration: number;
		originalIndex: number;
	};
	type GapInsertionGroup = {
		end: number;
		insertions: GapInsertionRecord[];
	};
	const insertionGroups = new Map<string, GapInsertionGroup>();

	for (let insertionIndex = 0; insertionIndex < insertions.length; insertionIndex += 1) {
		const insertion = insertions[insertionIndex];
		const atSec = Number(insertion?.atSec ?? Number.NaN);
		const duration = Number(insertion?.durationSec ?? Number.NaN);
		if (!Number.isFinite(atSec) || !Number.isFinite(duration) || duration <= EPS) continue;

		const isTrailingInsertion = Math.abs(atSec - clipOutSec) <= EPS;
		let followingSegmentIndex = -1;
		if (!isTrailingInsertion) {
			followingSegmentIndex = sortedSegments.findIndex((segment) => {
				const sourceStart = Number(segment?.srcStartSec ?? Number.NaN);
				return Number.isFinite(sourceStart) && sourceStart + EPS >= atSec;
			});
		}
		// Only an insertion anchored at clip-out extends the timeline by itself.
		// Any other insertion with no following derived segment was swallowed by
		// a trailing cut and therefore has no composition-time interval to render.
		if (!isTrailingInsertion && followingSegmentIndex < 0) continue;

		const key = isTrailingInsertion ? "trailing" : `segment:${followingSegmentIndex}`;
		const followingSegment =
			followingSegmentIndex >= 0 ? sortedSegments[followingSegmentIndex] : null;
		const rawEnd = followingSegment
			? Number(followingSegment?.compStartSec) - clipStartSec
			: safeDuration;
		if (!Number.isFinite(rawEnd)) continue;

		const group = insertionGroups.get(key) ?? {
			end: clamp(rawEnd, 0, safeDuration),
			insertions: [],
		};
		group.insertions.push({
			insertionId: String(insertion?.id ?? insertionIndex),
			atSec,
			duration,
			originalIndex: insertionIndex,
		});
		insertionGroups.set(key, group);
	}

	for (const group of insertionGroups.values()) {
		group.insertions.sort(
			(first, second) =>
				first.atSec - second.atSec || first.originalIndex - second.originalIndex,
		);
		const totalDuration = group.insertions.reduce(
			(total, insertion) => total + insertion.duration,
			0,
		);
		let cursor = clamp(group.end - totalDuration, 0, safeDuration);

		for (const insertion of group.insertions) {
			// Insertion duration is already composition-time duration. Insertions
			// swallowed by the same later cut share a final segment boundary, so
			// allocate them cumulatively in their source order instead of stacking
			// overlapping rectangles at that boundary.
			const end = clamp(cursor + insertion.duration, 0, group.end);
			if (end - cursor > EPS) {
				gaps.push({
					id: `gap:${insertion.insertionId}`,
					insertionId: insertion.insertionId,
					start: cursor,
					end,
				});
			}
			cursor = end;
		}
	}
    gaps.sort((a, b) => a.start - b.start);

    const sectionPoints = [
        0,
        safeDuration,
        ...sceneBoundaries.map((boundary) => boundary.time),
        ...gaps.flatMap((gap) => [gap.start, gap.end]),
    ].sort((a, b) => a - b);
    const uniqueSectionPoints: number[] = [];
    for (const point of sectionPoints) {
        if (!Number.isFinite(point)) continue;
        const clampedPoint = clamp(point, 0, safeDuration);
        if (
            !uniqueSectionPoints.length ||
            Math.abs(clampedPoint - uniqueSectionPoints[uniqueSectionPoints.length - 1]) > EPS
        ) {
            uniqueSectionPoints.push(clampedPoint);
        }
    }

    const sceneLabels = new Map<number, string>();
    try {
        const scenes: any[] =
            typeof clip?.getScenes === "function" && Array.isArray(clip.getScenes())
                ? clip.getScenes()
                : [];
        for (const scene of scenes) {
            const index = Number(scene?.index ?? Number.NaN);
            const label = String(scene?.label ?? "").trim();
            if (Number.isFinite(index) && label) sceneLabels.set(index, label);
        }
    } catch {}

    const scenePartCounts = new Map<number, number>();
    const sections: ScriptTimelineSection[] = [];
    for (let pointIndex = 0; pointIndex < uniqueSectionPoints.length - 1; pointIndex += 1) {
        const start = uniqueSectionPoints[pointIndex];
        const end = uniqueSectionPoints[pointIndex + 1];
        if (end - start <= EPS) continue;

        const gap = gaps.find(
            (candidate) =>
                Math.abs(candidate.start - start) <= EPS &&
                Math.abs(candidate.end - end) <= EPS,
        );
        if (gap) {
            sections.push({
                id: gap.id,
                kind: "gap",
                start,
                end,
                sceneIndex: null,
				sceneIdentity: null,
                label: "Gap",
                gapInsertionId: gap.insertionId,
            });
            continue;
        }

        const midpoint = start + (end - start) / 2;
        let sceneIndex = 0;
		let activeSceneBoundary: ScriptTimelineSceneBoundary | null = null;
        for (const boundary of sceneBoundaries) {
			if (midpoint >= boundary.time - EPS) {
				sceneIndex = boundary.sceneIndex;
				activeSceneBoundary = boundary;
			}
            else break;
        }
		const sceneIdentity = activeSceneBoundary
			? `split:${activeSceneBoundary.splitId}`
			: "root";
		const sceneStart = activeSceneBoundary?.time ?? 0;
		const precedingGap = [...gaps]
			.reverse()
			.find(
				(candidate) =>
					candidate.end <= start + EPS &&
					candidate.end >= sceneStart - EPS,
			);
		const fragmentIdentity = precedingGap
			? `after-gap:${precedingGap.insertionId}`
			: "head";
        const partIndex = scenePartCounts.get(sceneIndex) ?? 0;
        scenePartCounts.set(sceneIndex, partIndex + 1);
        sections.push({
			id: `scene:${sceneIdentity}:${fragmentIdentity}`,
            kind: "scene",
            start,
            end,
            sceneIndex,
			sceneIdentity,
            label:
                partIndex === 0
                    ? sceneLabels.get(sceneIndex) || `Scene ${sceneIndex + 1}`
                    : undefined,
        });
    }

    return {
        duration: safeDuration,
        sections,
        sceneBoundaries,
        editBoundaries,
        gaps,
    };
};

export const findScriptTimelineSectionAtTime = (
    sections: ScriptTimelineSection[],
    time: number,
): ScriptTimelineSection | null => {
    if (!sections.length || !Number.isFinite(time)) return null;
    const last = sections[sections.length - 1];
    if (time >= last.end - EPS) return last;
    return sections.find((section) => time >= section.start && time < section.end) ?? null;
};

/**
 * Resolve the transcript range owned by a structural ScriptClip selection.
 *
 * A gap is one exact selectable section. A logical scene may be rendered as
 * multiple sections when inserted gaps divide it, but selecting any fragment
 * still owns the full scene range. Stable identities deliberately take
 * precedence over scene indexes so inserting an earlier scene cannot retarget
 * an existing selection.
 */
export const getScriptTimelineSelectionRange = (
    sections: ScriptTimelineSection[],
    selectedSection: ScriptTimelineSection | null | undefined,
): ScriptTimelineRange | null => {
    if (!selectedSection) return null;

    if (selectedSection.kind === "gap") {
        const gap = selectedSection.gapInsertionId
            ? sections.find(
                (section) =>
                    section.kind === "gap" &&
                    section.gapInsertionId === selectedSection.gapInsertionId,
            )
            : sections.find(
                (section) => section.kind === "gap" && section.id === selectedSection.id,
            );
        if (!gap || !Number.isFinite(gap.start) || !Number.isFinite(gap.end)) return null;
        return gap.end - gap.start > EPS ? { start: gap.start, end: gap.end } : null;
    }

    let sceneSections: ScriptTimelineSection[] = [];
    if (selectedSection.sceneIdentity) {
        sceneSections = sections.filter(
            (section) =>
                section.kind === "scene" &&
                section.sceneIdentity === selectedSection.sceneIdentity,
        );
    } else {
        const exactSection = sections.find(
            (section) => section.kind === "scene" && section.id === selectedSection.id,
        );
        const legacySceneIndex = exactSection?.sceneIndex ?? selectedSection.sceneIndex;
        sceneSections = typeof legacySceneIndex === "number"
            ? sections.filter(
                (section) =>
                    section.kind === "scene" && section.sceneIndex === legacySceneIndex,
            )
            : exactSection
                ? [exactSection]
                : [];
    }

    const validSceneSections = sceneSections.filter(
        (section) =>
            Number.isFinite(section.start) &&
            Number.isFinite(section.end) &&
            section.end - section.start > EPS,
    );
    if (!validSceneSections.length) return null;

    return {
        start: Math.min(...validSceneSections.map((section) => section.start)),
        end: Math.max(...validSceneSections.map((section) => section.end)),
    };
};

export const rangesMatch = (
    first: { start: number; end: number } | null | undefined,
    second: { start: number; end: number } | null | undefined,
    tolerance = 0.01,
): boolean => {
    if (!first || !second) return false;
    return (
        Math.abs(Number(first.start) - Number(second.start)) <= tolerance &&
        Math.abs(Number(first.end) - Number(second.end)) <= tolerance
    );
};

export const normalizeScriptTimelineRange = (
    range: ScriptTimelineRange | null | undefined,
    duration: number,
): ScriptTimelineRange | null => {
    if (!range || !Number.isFinite(duration) || duration <= 0) return null;
    const rawStart = Number(range.start);
    const rawEnd = Number(range.end);
    if (!Number.isFinite(rawStart) || !Number.isFinite(rawEnd)) return null;
    const start = clamp(Math.min(rawStart, rawEnd), 0, duration);
    const end = clamp(Math.max(rawStart, rawEnd), 0, duration);
    return end - start > EPS ? { start, end } : null;
};

export const isScriptTimelineRangeDragActivated = (
    mode: ScriptTimelineRangeDragMode,
    startClientX: number,
    currentClientX: number,
): boolean =>
    mode !== "create" ||
    Math.abs(currentClientX - startClientX) >= SCRIPT_TIMELINE_RANGE_DRAG_THRESHOLD_PX;

/** Pure range geometry shared by live preview and pointer-up commit. */
export const calculateScriptTimelineRangeDrag = ({
    mode,
    anchorTime,
    pointerTime,
    duration,
    initialRange,
    activated = true,
}: {
    mode: ScriptTimelineRangeDragMode;
    anchorTime: number;
    pointerTime: number;
    duration: number;
    initialRange: ScriptTimelineRange | null;
    activated?: boolean;
}): ScriptTimelineRange | null => {
    const initial = normalizeScriptTimelineRange(initialRange, duration);
    const pointer = clamp(Number.isFinite(pointerTime) ? pointerTime : 0, 0, duration);

    if (mode === "create") {
        return activated
            ? normalizeScriptTimelineRange({ start: anchorTime, end: pointer }, duration)
            : initial;
    }
    if (!initial) return null;

    if (mode === "resize-start") {
		const minimumWidth = Math.min(SCRIPT_TIMELINE_MIN_RANGE_SEC, initial.end);
        return {
			start: clamp(pointer, 0, Math.max(0, initial.end - minimumWidth)),
            end: initial.end,
        };
    }
    if (mode === "resize-end") {
		const minimumWidth = Math.min(
			SCRIPT_TIMELINE_MIN_RANGE_SEC,
			Math.max(0, duration - initial.start),
		);
        return {
            start: initial.start,
			end: clamp(pointer, Math.min(duration, initial.start + minimumWidth), duration),
        };
    }

    const width = initial.end - initial.start;
    const rawStart = initial.start + (pointer - anchorTime);
    const start = clamp(rawStart, 0, Math.max(0, duration - width));
    return { start, end: start + width };
};
